import * as React from 'react';
import { useState } from 'react';
import {
    Box,
    Checkbox,
    Collapse,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SubscriberResource } from 'types/underwriting.type';
import { formatDollar } from 'helpers/formatters';

interface Props {
    carriers: SubscriberResource[];
    readOnly?: boolean;
    onChange?: (carriers: SubscriberResource[]) => void;
    isLoading?: boolean;
}

const UwCarrierSubscription: React.FC<Props> = ({ carriers, readOnly = false, onChange, isLoading = false }) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const handleToggle = () => setExpanded((prev) => !prev);

    const handleParticipatingChange = (index: number, checked: boolean) => {
        if (readOnly || !onChange) return;
        const updated = carriers.map((c, i) => (i === index ? { ...c, isParticipating: checked } : c));
        onChange(updated);
    };

    const participatingCount = carriers.filter((c) => c.isParticipating).length;

    return (
        <Box>
            <Grid container alignItems={'baseline'} spacing={0}>
                <Grid>
                    <Typography variant="body1">{`Carrier Subscription (${participatingCount})`}</Typography>
                </Grid>
                <Grid>
                    <IconButton
                        aria-label="expand carrier subscription"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggle();
                        }}
                        sx={{ ml: 0.5 }}
                    >
                        {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                </Grid>
            </Grid>

            <Collapse in={expanded}>
                <Table size="small" sx={{ mt: 1, maxWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>
                                <Typography variant="caption" fontWeight="bold">
                                    Carrier
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="caption" fontWeight="bold">
                                    Program Share %
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="caption" fontWeight="bold">
                                    Program Limit
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {carriers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body1" color="text.secondary">
                                        No carriers available.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {carriers.map((carrier, index) => (
                            <TableRow
                                key={carrier.carrierName + index}
                                sx={{ opacity: carrier.isParticipating ? 1 : 0.45 }}
                            >
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={carrier.isParticipating}
                                        disabled={readOnly || isLoading}
                                        color="secondary"
                                        size="small"
                                        onChange={(e) => handleParticipatingChange(index, e.target.checked)}
                                        inputProps={{ 'aria-label': `${carrier.carrierName} participating` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body1" sx={{ marginBottom: 0 }}>
                                        {carrier.carrierName}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" className={isLoading ? 'cell-loading' : ''}>
                                    <Typography variant="body1" sx={{ marginBottom: 0 }}>
                                        {isLoading ? (
                                            <Skeleton />
                                        ) : carrier.programPercentage != null ? (
                                            `${Number(carrier.programPercentage * 100).toLocaleString(undefined, {
                                                maximumFractionDigits: 4,
                                            })} %`
                                        ) : (
                                            '--'
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1" sx={{ marginBottom: 0 }}>
                                        {isLoading ? (
                                            <Skeleton />
                                        ) : carrier.programLimit != null ? (
                                            formatDollar(carrier.programLimit, 2)
                                        ) : (
                                            '--'
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Collapse>
        </Box>
    );
};

export default React.memo(UwCarrierSubscription);
